import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';
import { Department, DepartmentOrganizationUser } from './department';

@Injectable({ providedIn: 'root' })
export class DepartmentService {
  private apiLink = environment.apiLink;
  private _httpClient = inject(HttpClient);

  getAll(name?: string): Observable<Department[]> {
    let params = new HttpParams();
    if (name) {
      params = params.append('name', name);
    }
    return this._httpClient.get<{ data: { departments: Department[] } }>(this.apiLink + 'departments/', { params }).pipe(
      map(res => res.data.departments),
    );
  }

  getAllDivision(name?: string): Observable<Department[]> {
    let params = new HttpParams();
    if (name) {
      params = params.append('name', name);
    }
    return this._httpClient
      .get<{ data: { mainDepartments: Department[] } }>(this.apiLink + 'division/', { params })
      .pipe(map(res => res.data.mainDepartments));
  }

  /**
   * Organization users filtered by department and/or division (`users/get-organization-users`).
   * Uses repeated `departmentIds` and/or `divisionId` query params; response shape is checked
   * (route is untyped).
   */
  getOrganizationUsers(options: {
    departmentIds?: number[];
    divisionId?: number;
  }): Observable<DepartmentOrganizationUser[]> {
    let params = new HttpParams().set('shouldSelfInclude', 'false');
    (options.departmentIds ?? []).forEach((departmentId) => {
      params = params.append('departmentIds', departmentId.toString());
    });
    if (options.divisionId != null && Number.isFinite(options.divisionId) && options.divisionId > 0) {
      params = params.set('divisionId', options.divisionId.toString());
    }
    return this._httpClient.get<unknown>(this.apiLink + 'users/get-organization-users', { params }).pipe(
      map(response => this._readOrganizationUsers(response)),
    );
  }

  create(modal: unknown) {
    return this._httpClient.post(this.apiLink + 'departments/new-department', modal);
  }

  update(modal: unknown, id: number) {
    return this._httpClient.put(this.apiLink + `departments/${id}/edit-department`, modal);
  }

  delete(id: number) {
    return this._httpClient.delete(this.apiLink + `departments/${id}/delete-department`);
  }

  createDivision(modal: unknown) {
    return this._httpClient.post(this.apiLink + 'division/new-department', modal);
  }

  updateDivision(modal: unknown, id: number) {
    return this._httpClient.put(this.apiLink + `division/${id}/edit-department`, modal);
  }

  deleteDivision(id: number) {
    return this._httpClient.delete(this.apiLink + `division/${id}/delete-department`);
  }

  private _readOrganizationUsers(response: unknown): DepartmentOrganizationUser[] {
    const seen = new Set<number>();
    const users: DepartmentOrganizationUser[] = [];
    for (const row of this._rowsOf(response)) {
      const id = Number(row['id']);
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) {
        continue;
      }
      const role = `${row['role'] ?? ''}`.trim();
      // Hierarchy list is managers/staff only — drop organization admins client-side
      // (`shouldSelfInclude` stays false; this also removes other admins in the payload).
      if (this._isOrganizationAdminRole(role)) {
        continue;
      }
      const name =
        `${row['name'] ?? ''}`.trim() ||
        `${row['firstName'] ?? ''} ${row['lastName'] ?? ''}`.trim() ||
        `${row['email'] ?? ''}`.trim();
      if (!name) {
        continue;
      }
      seen.add(id);
      users.push({
        id,
        name,
        email: `${row['email'] ?? ''}`.trim(),
        role,
        departments: this._departmentNames(row['departments']),
        initials: this._initials(name),
      });
    }
    return users;
  }

  private _isOrganizationAdminRole(role: string): boolean {
    const normalized = role.trim().toLowerCase();
    return (
      normalized === 'user' ||
      normalized === 'company' ||
      normalized === 'organization admin' ||
      normalized === 'organizationadmin' ||
      normalized === 'admin'
    );
  }

  private _rowsOf(response: unknown): Record<string, unknown>[] {
    if (!response || typeof response !== 'object') {
      return [];
    }
    const data = (response as { data?: unknown }).data;
    const candidate =
      data && typeof data === 'object' && Array.isArray((data as { users?: unknown }).users)
        ? (data as { users: unknown[] }).users
        : Array.isArray(data)
          ? data
          : [];
    return candidate.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object');
  }

  private _departmentNames(departments: unknown): string[] {
    if (!Array.isArray(departments) || !departments.length) {
      return [];
    }
    const names: string[] = [];
    const seen = new Set<string>();
    for (const entry of departments) {
      let name = '';
      if (typeof entry === 'string') {
        name = entry.trim();
      } else if (entry && typeof entry === 'object') {
        const rawName = (entry as { name?: unknown }).name;
        if (typeof rawName === 'string') {
          name = rawName.trim();
        } else if (rawName && typeof rawName === 'object') {
          const en = (rawName as { en?: unknown }).en;
          if (typeof en === 'string') {
            name = en.trim();
          }
        }
      }
      if (!name || seen.has(name)) {
        continue;
      }
      seen.add(name);
      names.push(name);
    }
    return names;
  }

  private _initials(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
  }
}

